import { Vector3 } from 'three';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Vertex } from '@/thirdPartyLibraries/three-mesh-halfedge/src';

export interface ScaleVerticesOptions {
  /** Uniform scale or per-axis scale factors. Required. */
  factor: number | Vector3;
  /** Center of scaling. Defaults to centroid of the provided vertices. */
  center?: Vector3;
  /** Axes mask; if false for an axis, that axis is left unchanged. Defaults to all true. */
  axes?: { x?: boolean; y?: boolean; z?: boolean };
  /** If true, updates BufferGeometry from halfedges after scaling. Default false. */
  updateGeometry?: boolean;
}

/** Scales the given vertices about a center. */
export function scaleVertices(
  mesh: HalfEdgeMesh,
  vertices: Vertex[],
  opts: ScaleVerticesOptions,
): void {
  if (!vertices || vertices.length === 0) return;
  const factor = opts.factor instanceof Vector3 ? opts.factor.clone() : new Vector3(opts.factor, opts.factor, opts.factor);
  const axes = { x: true, y: true, z: true, ...(opts.axes ?? {}) } as Required<NonNullable<ScaleVerticesOptions['axes']>>;

  const center = opts.center?.clone() ?? (() => {
    const c = new Vector3();
    for (const v of vertices) c.add(v.position);
    return c.multiplyScalar(1 / vertices.length);
  })();

  for (const v of vertices) {
    const d = v.position.clone().sub(center);
    const nx = center.x + (axes.x ? d.x * factor.x : d.x);
    const ny = center.y + (axes.y ? d.y * factor.y : d.y);
    const nz = center.z + (axes.z ? d.z * factor.z : d.z);
    v.position.set(nx, ny, nz);
  }

  if (opts.updateGeometry) {
    mesh.updateGeometryFromHalfedges();
  } else {
    mesh.calculateBorderVertices();
  }
}

